import {
  Dispatch,
  FC,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { RELEVANCES } from 'constants/date';
import {
  MAP_ENTITY_FILTERABLE_PARAMS,
  MAP_ENTITY_PARAM_VALUES,
  predefinedTemplates,
} from 'constants/entities';
import { MAP_ENTITIES_LOADER } from 'constants/loaders';
import { useAppDispatch, useAppSelector } from 'hooks';
import { FilterCriteria } from 'interfaces';
import { getFilteredMapEntitiesThunk } from 'store/slices/mapV2/tabsReducer/layersReducer/mapEntitiesSlice/actions';
import {
  countersSelector,
  entitiesSelector,
  hierarchySelector,
  predefinedTemplateSelector,
} from 'store/slices/mapV2/tabsReducer/layersReducer/mapEntitiesSlice/selectors';
import { stateActions } from 'store/slices/mapV2/tabsReducer/layersReducer/stateSlice';
import {
  filtersSelector,
  mapDndStateSelector,
} from 'store/slices/mapV2/tabsReducer/layersReducer/stateSlice/selectors';
import { loaderSelector } from 'store/slices/service/loadersSlice/selectors';
import { EntityParameter } from 'types';
import { useDebouncedCallback } from 'use-debounce';

import { AccessControlDetailsProps } from 'components/Access/AccessControlDetails';
import { LayerDetailsProps } from 'components/Map/LayerDetails';
import { Loader, TextInput } from 'components/ui';
import { FilterDropdown } from 'components/ui/Filters';
import { ActiveFilterCriterias } from 'components/ui/Filters/ActiveFilterCriterias';
import { ActiveFilterCriteria } from 'components/ui/Filters/types';
import {
  addMilitaryHierarchyChildrenToFilterCriteria,
  getFilterCriteria,
} from 'components/ui/Filters/utils';
import {
  getEntityParametersIdToTitleMap,
  getEntityParametersTitleToIdMap,
  isWholeNumber,
} from 'utils';

import { ImportDetailsProps } from '../../ImportDetails/ImportDetails';
import { UpdateLayerChildrenDetailsProps } from '../../UpdateLayerChildrenDetails/UpdateLayerChildrenDetails';

import { EntitiesTree } from './EntitiesTree';
import { LayersFooter } from './LayersFooter';
import { QuickFilters } from './QuickFilters';
import {
  ActiveFilterCriteriasMap,
  CreatedAtFilter,
  EntitiesTreeRef,
} from './types';
import {
  getEntitiesTree,
  getFilterCriteriaParam,
  getFilterCriteriaParams,
} from './utils';

export interface SidebarContentProps {
  isPublic: boolean;
  setLayerDetailsProps: Dispatch<SetStateAction<LayerDetailsProps | undefined>>;
  setAccessControlDetailsProps: Dispatch<
    SetStateAction<AccessControlDetailsProps | undefined>
  >;
  setImportDetailsProps: Dispatch<
    SetStateAction<ImportDetailsProps | undefined>
  >;
  search: string;
  setSearch: (input: string) => void;
  activeFilterCriteriasMap: ActiveFilterCriteriasMap;
  setActiveFilterCriteriasMap: Dispatch<
    SetStateAction<ActiveFilterCriteriasMap>
  >;
  setUpdateLayerChildrenDetailsProps: Dispatch<
    SetStateAction<UpdateLayerChildrenDetailsProps | undefined>
  >;
  quickFiltersProps: {
    createdAtFilter: CreatedAtFilter;
    onCreatedAtFilterChange: Dispatch<SetStateAction<CreatedAtFilter>>;
    backgroundFilterCriteriasMap: ActiveFilterCriteriasMap;
    setBackgroundFilterCriteriasMap: Dispatch<
      SetStateAction<ActiveFilterCriteriasMap>
    >;
  };
}

export const getFilterableParameters = (
  parameters: EntityParameter[],
  filterableParametersMap: Record<number, string>
): EntityParameter[] =>
  parameters.filter((parameter) => filterableParametersMap[parameter.id]);

const SidebarContent: FC<SidebarContentProps> = ({
  isPublic,
  setLayerDetailsProps,
  setAccessControlDetailsProps,
  setImportDetailsProps,
  search,
  setSearch,
  activeFilterCriteriasMap,
  setActiveFilterCriteriasMap,
  setUpdateLayerChildrenDetailsProps,
  quickFiltersProps: {
    createdAtFilter,
    onCreatedAtFilterChange,
    backgroundFilterCriteriasMap,
    setBackgroundFilterCriteriasMap,
  },
}) => {
  const objectTemplate = useAppSelector((state) =>
    predefinedTemplateSelector(state, predefinedTemplates.MAP_OBJECT)
  );
  const objectParameters = objectTemplate?.parameters || [];
  const entities = useAppSelector(entitiesSelector);
  const counters = useAppSelector(countersSelector);
  const hierarchy = useAppSelector(hierarchySelector);
  const filters = useAppSelector(filtersSelector);
  const isLoading = useAppSelector((state) =>
    loaderSelector(state, MAP_ENTITIES_LOADER)
  );
  const treeRef = useRef<EntitiesTreeRef>();
  const { hasTabSwitched } = useAppSelector(mapDndStateSelector);

  const dispatch = useAppDispatch();

  const entityFilterableParametersMap = useMemo(
    () =>
      getEntityParametersIdToTitleMap(
        objectParameters,
        MAP_ENTITY_FILTERABLE_PARAMS
      ),
    [objectParameters]
  );

  const entityFilterableParameters = useMemo(
    () =>
      getFilterableParameters(objectParameters, entityFilterableParametersMap),
    [objectParameters]
  );

  const filterCriterias = useMemo(
    () =>
      entityFilterableParameters.map((parameter) =>
        getFilterCriteria(parameter)
      ),
    [entityFilterableParameters]
  );

  const objectParametersIdTitleMap = useMemo(
    () =>
      getEntityParametersTitleToIdMap(
        objectTemplate?.parameters ?? [],
        MAP_ENTITY_PARAM_VALUES
      ),
    [objectTemplate]
  );

  const activeFilterCriteriasList = useMemo(
    () => getFilterCriteriaParams(activeFilterCriteriasMap),
    [activeFilterCriteriasMap]
  );

  const backgroundFilterCriteriasList = useMemo(
    () => getFilterCriteriaParams(backgroundFilterCriteriasMap),
    [backgroundFilterCriteriasMap]
  );

  const entitiesTreeData = useMemo(
    () => getEntitiesTree(entities, counters, hierarchy),
    [entities, counters, hierarchy]
  );

  const treeData = entitiesTreeData.filter(
    (el) => el.entity.isPersonalSpace !== isPublic
  );

  const handleTreeRef = useCallback((ref: EntitiesTreeRef) => {
    treeRef.current = ref;
  }, []);

  const handleFilter = (
    value: string,
    filterCriteria: FilterCriteria[],
    createdAtFilter: CreatedAtFilter
  ) => {
    const shouldApplyCreatedAtFilter =
      createdAtFilter && createdAtFilter?.relevance !== RELEVANCES.ALL_TIME;

    const updatedFilterCriteria = filterCriteria.map((fc) => {
      const paramName = entityFilterableParametersMap[fc.parameterID];
      const param = entityFilterableParameters.find(
        (p) => p.title === paramName
      );

      if (param && param.settings.militaryClass?.length) {
        return addMilitaryHierarchyChildrenToFilterCriteria(
          fc,
          param.settings.militaryClass
        );
      }

      return fc;
    });

    const queryOrIdParam = isWholeNumber(value)
      ? {
          idQuery: String(parseInt(value, 10)),
        }
      : {
          query: value,
        };

    const filterCriteriasParam = !!filterCriterias.length && {
      filterCriteria: updatedFilterCriteria,
    };

    const createdAtFilterParam = shouldApplyCreatedAtFilter && {
      createdAtFilter: {
        gte: createdAtFilter?.value?.[0],
        lte: createdAtFilter?.value?.[1],
      },
    };

    const filters = {
      ...(value.trim() && queryOrIdParam),
      ...filterCriteriasParam,
      ...createdAtFilterParam,
    };

    dispatch(stateActions.setFilters({ ...filters }));
    dispatch(
      getFilteredMapEntitiesThunk({
        ...filters,
      })
    );
  };

  const onFiltersChange = useCallback(() => {
    treeRef.current?.closeAll?.();
  }, []);

  const handleSearch = useDebouncedCallback(
    (value: string, filterCriterias: FilterCriteria[]) =>
      handleFilter(
        value,
        [...backgroundFilterCriteriasList, ...filterCriterias],
        createdAtFilter
      ),
    700
  );

  const handleSearchChange = (value: string) => {
    setSearch(value);
    handleSearch(value, activeFilterCriteriasList);
  };

  const handleFilterCriteriaApply = (
    criteria: ActiveFilterCriteria,
    isBackground?: boolean
  ) => {
    const { [criteria.id]: deselected, ...restCriterias } = isBackground
      ? backgroundFilterCriteriasMap
      : activeFilterCriteriasMap;

    isBackground
      ? setBackgroundFilterCriteriasMap((state) => ({
          ...state,
          [criteria.id]: criteria,
        }))
      : setActiveFilterCriteriasMap((state) => ({
          ...state,
          [criteria.id]: criteria,
        }));

    handleFilter(
      search,
      isBackground
        ? [
            ...backgroundFilterCriteriasList,
            ...getFilterCriteriaParams(restCriterias),
            getFilterCriteriaParam(criteria),
          ]
        : [
            ...backgroundFilterCriteriasList,
            ...getFilterCriteriaParams(restCriterias),
            getFilterCriteriaParam(criteria),
          ],
      createdAtFilter
    );
  };

  const handleFilterCriteriaRemove = (
    criteria: ActiveFilterCriteria,
    isBackground?: boolean
  ) => {
    const { [criteria.id]: deselected, ...restCriterias } = isBackground
      ? backgroundFilterCriteriasMap
      : activeFilterCriteriasMap;
    isBackground
      ? setBackgroundFilterCriteriasMap(restCriterias)
      : setActiveFilterCriteriasMap(restCriterias);
    const combinedCriteria = isBackground
      ? activeFilterCriteriasList.concat(getFilterCriteriaParams(restCriterias))
      : getFilterCriteriaParams(restCriterias).concat(
          backgroundFilterCriteriasList
        );
    handleFilter(search, combinedCriteria, createdAtFilter);
  };

  const handleEntityCreatedAtFilter = (createdAtFilter: CreatedAtFilter) => {
    onCreatedAtFilterChange(createdAtFilter);
    handleFilter(
      search,
      [...activeFilterCriteriasList, ...backgroundFilterCriteriasList],
      createdAtFilter
    );
  };

  const handleAddProject = () =>
    setLayerDetailsProps({ onClose: () => setLayerDetailsProps(undefined) });

  const handleImportKML = () =>
    setImportDetailsProps({
      parentEntityID: 0,
      onClose: () => setImportDetailsProps(undefined),
    });

  useEffect(() => {
    treeRef.current?.hideCursor?.();
  }, [hasTabSwitched]);

  useEffect(() => {
    onFiltersChange();
  }, [filters, onFiltersChange]);

  return (
    <div className="flex flex-col h-full">
      <QuickFilters onCreatedAtFilterChange={handleEntityCreatedAtFilter} />
      <TextInput
        value={search}
        placeholder="Поиск по названию и ID"
        searchIcon
        classNames={{ container: 'w-full px-4 pt-2' }}
        onChange={handleSearchChange}
      >
        <FilterDropdown
          criterias={filterCriterias}
          onCriteriaApply={handleFilterCriteriaApply}
        />
      </TextInput>
      <ActiveFilterCriterias
        activeCriterias={Object.values(activeFilterCriteriasMap)}
        onCriteriaRemove={handleFilterCriteriaRemove}
      />
      {isLoading && (
        <Loader className="absolute h-full top-0 z-10 bg-dark rounded-[10px]" />
      )}
      <EntitiesTree
        title={isPublic ? 'База знаний' : 'Личное пространство'}
        data={treeData}
        objectTemplate={objectTemplate}
        entities={entities}
        counters={counters}
        objectParametersMap={objectParametersIdTitleMap}
        search={search}
        isPublic={isPublic}
        onRef={handleTreeRef}
        onChangeLayerDetailsProps={setLayerDetailsProps}
        onChangeAccessControlDetailsProps={setAccessControlDetailsProps}
        onChangeImportDetailsProps={setImportDetailsProps}
        onChangeUpdateLayerChildrenDetailsProps={
          setUpdateLayerChildrenDetailsProps
        }
      />
      <LayersFooter
        onAddProject={handleAddProject}
        onImportKML={handleImportKML}
      />
    </div>
  );
};

export default SidebarContent;
